import type {
  AdminAuditTypeEnvelope,
  AdminAuditTypesResponse,
  CreateAdminAuditTypeBody,
  PatchAdminAuditTypeBody,
} from '@/api/(admin)/audit-types/types';
import { BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/** Pure RPC for the admin audit-types page, all on the modern TS API under `BACKEND_URL`. */

/** The list. Takes a pre-built query string; paging, sort and filters live in the hook. */
export async function fetchAdminAuditTypes(
  qs: string,
): Promise<AdminAuditTypesResponse> {
  return apiFetch<AdminAuditTypesResponse>(
    `/admin/audit-types?${qs}`,
    {},
    BACKEND_URL,
  );
}

/** Creates one. Returns the server envelope as-is. */
export async function createAdminAuditType(
  body: CreateAdminAuditTypeBody,
): Promise<AdminAuditTypeEnvelope> {
  return apiFetch<AdminAuditTypeEnvelope>(
    '/admin/audit-types',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/** A partial update — the route writes only the keys it is given. */
export async function patchAdminAuditType(
  id: string,
  body: PatchAdminAuditTypeBody,
): Promise<AdminAuditTypeEnvelope> {
  return apiFetch<AdminAuditTypeEnvelope>(
    `/admin/audit-types/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/** Deletes one. 204 on success; 400 `used_audit_type` while audits reference it. */
export async function deleteAdminAuditType(id: string): Promise<void> {
  return apiFetch<void>(
    `/admin/audit-types/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    BACKEND_URL,
  );
}

/** Marks a type deprecated, or takes the mark off. */
export async function updateAdminAuditTypeUsage(
  id: string,
  deprecate: boolean,
): Promise<AdminAuditTypeEnvelope> {
  return apiFetch<AdminAuditTypeEnvelope>(
    `/admin/audit-types/${encodeURIComponent(id)}/usage`,
    { method: 'PATCH', body: JSON.stringify({ deprecate }) },
    BACKEND_URL,
  );
}
